import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { HelmetProvider } from 'react-helmet-async';

// Suppress benign "ResizeObserver loop completed with undelivered notifications"
// warning fired by third-party widgets (e.g. Elfsight). CRA's dev overlay
// treats it as a runtime error even though it does not indicate a real bug.

// 1) Root-cause fix: defer ResizeObserver callbacks to the next animation
//    frame so they cannot trigger the loop-limit warning.
if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function') {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let rafId = 0;
      const wrapped = (entries, observer) => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          try {
            callback(entries, observer);
          } catch (err) {
            const message = err && err.message ? err.message : String(err);
            if (!message.includes('ResizeObserver loop')) throw err;
          }
        });
      };
      super(wrapped);
    }
  };
}

// 2) Belt-and-braces: also swallow the error if it still reaches window.
const RESIZE_OBSERVER_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

const isResizeObserverMessage = (msg) =>
  typeof msg === 'string' && RESIZE_OBSERVER_ERRORS.some((m) => msg.includes(m));

window.addEventListener(
  'error',
  (e) => {
    if (isResizeObserverMessage(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true
);

window.addEventListener(
  'unhandledrejection',
  (e) => {
    const reason = e.reason;
    const msg = reason && reason.message ? reason.message : String(reason);
    if (isResizeObserverMessage(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true
);

// 3) CRA's dev overlay also listens to console.error. Filter that too.
const originalConsoleError = console.error;
console.error = (...args) => {
  const first = args[0];
  const msg = typeof first === 'string' ? first : first && first.message;
  if (isResizeObserverMessage(msg)) return;
  originalConsoleError.apply(console, args);
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
